<template>
  <div class="card p-4">
    <!-- Download Sample CSV -->
    <div class="flex justify-end mb-4">
      <Button size="small" label="Download Sample CSV" icon="pi pi-download" @click="downloadSampleCSV" data-testid="btn-student-import-sample" />
    </div>

    <div class="flex flex-col gap-6">
      <!-- File Upload -->
      <div class="flex flex-col gap-2">
        <div
          class="border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center transition-colors cursor-pointer"
          :class="isDragging ? 'border-primary-500 bg-primary-50' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'"
          @click="triggerFileInput"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="onDrop"
          data-testid="btn-student-import-upload-trigger">
          <i class="pi pi-cloud-upload text-4xl mb-2" :class="isDragging ? 'text-primary-500' : 'text-gray-400'"></i>
          <p class="font-medium" :class="isDragging ? 'text-primary-700' : 'text-gray-600'">
            {{ isDragging ? 'Drop file here' : 'Click or drag and drop to select a file' }}
          </p>
          <p class="text-xs mt-1" :class="isDragging ? 'text-primary-500' : 'text-gray-400'">Supported formats: .csv, .xls, .xlsx</p>
          <input type="file" ref="fileInput" class="hidden" accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" @change="onFileChange" />
        </div>

        <div v-if="fileName" class="flex items-center gap-2 text-primary bg-primary/10 p-3 rounded-lg">
          <i class="pi pi-file"></i>
          <span class="font-semibold">{{ fileName }}</span>
          <i v-if="isParsing" class="pi pi-spin pi-spinner ml-2"></i>
          <span v-else class="text-xs text-primary ml-auto">{{ parsedRows.length }} rows detected</span>
        </div>
      </div>

      <!-- Configuration Fields -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
        <div class="flex flex-col gap-2">
          <label class="font-semibold text-gray-700">Academic Calendar</label>
          <AcademicCalendarSelect v-model="academicCalendarId" placeholder="Select Academic Calendar" />
          <p class="text-xs text-gray-400">If not selected, the school's active calendar will be used</p>
        </div>

        <template v-if="isTertiary">
          <div class="flex flex-col gap-2">
            <label class="font-semibold text-gray-700">Programme <span class="text-red-500">*</span></label>
            <Select v-model="programmeId" :options="programmeOptions" optionLabel="label" optionValue="id"
              placeholder="Select Programme" class="w-full" filter />
          </div>
          <div class="flex flex-col gap-2">
            <label class="font-semibold text-gray-700">Level <span class="text-red-500">*</span></label>
            <Select v-model="classroomSectionId" :options="levelOptions" optionLabel="name" optionValue="id"
              placeholder="Select Level" class="w-full" filter />
          </div>
          <div class="flex flex-col gap-2" v-if="campuses.length > 0">
            <label class="font-semibold text-gray-700">Campus</label>
            <Select v-model="campusId" :options="campuses" optionLabel="name" optionValue="id"
              placeholder="Select Campus" class="w-full" filter />
          </div>
        </template>

        <template v-else>
          <div class="flex flex-col gap-2">
            <label class="font-semibold text-gray-700">Classroom Section <span class="text-red-500">*</span></label>
            <Select v-model="classroomSectionId" :options="classroomSections" optionLabel="name" optionValue="id"
              placeholder="Select Classroom Section" class="w-full" filter />
          </div>
        </template>
      </div>

      <!-- Mapping Table -->
      <div v-if="parsedRows.length" class="flex flex-col gap-4">
        <div>
          <h3 class="text-xl font-bold text-gray-800">Map File Headers to System Fields</h3>
          <p class="text-gray-600 text-sm">
            Match the columns from your file to the corresponding system fields.
            Fields marked <span class="text-red-500 font-semibold">*</span> are required.
          </p>
        </div>

        <div v-if="missingRequiredFields.length" class="flex items-start gap-2 p-3 rounded-lg bg-orange-50 border border-orange-200 text-sm" data-testid="student-import-missing-required">
          <i class="pi pi-exclamation-triangle text-orange-600 mt-0.5"></i>
          <div>
            <p class="font-semibold text-orange-800">
              You haven't mapped {{ missingRequiredFields.length }} required field{{ missingRequiredFields.length === 1 ? '' : 's' }}.
            </p>
            <p class="text-orange-700">
              Map a column for: <span class="font-medium">{{ missingRequiredFields.map((f) => f.label).join(', ') }}</span>.
              Without these, every row will fail validation.
            </p>
          </div>
        </div>

        <div v-if="unmappedRecommended.length" class="flex items-start gap-2 p-3 rounded-lg bg-surface-50 border border-surface-200 text-sm"
          data-testid="student-import-unmapped-required-custom">
          <i class="pi pi-info-circle text-primary mt-0.5"></i>
          <div>
            <p class="font-semibold text-surface-800">
              {{ unmappedRecommended.length }} required custom field{{ unmappedRecommended.length === 1 ? ' is' : 's are' }} not mapped.
            </p>
            <p class="text-surface-600">
              {{ unmappedRecommendedNames }}.
              Students will still import, but these details will be blank until someone fills them in on each profile.
            </p>
          </div>
        </div>

        <div v-if="subjectColumns.length" class="flex items-start gap-2 p-3 rounded-lg bg-primary/10 border border-primary/20 text-sm">
          <i class="pi pi-info-circle text-primary mt-0.5"></i>
          <div>
            <p class="font-semibold text-gray-800">
              {{ subjectColumns.length }} subject column{{ subjectColumns.length === 1 ? '' : 's' }} detected — students will be auto-enrolled.
            </p>
            <p class="text-gray-600">{{ subjectColumns.join(', ') }}. Unknown subject/course names are silently skipped.</p>
          </div>
        </div>

        <DataTable size="small" :value="modelFields" responsiveLayout="scroll" showGridlines class="p-datatable-sm">
          <Column header="System Field" class="font-semibold bg-gray-50">
            <template #body="slotProps">
              <span>{{ slotProps.data.label }}</span>
              <span v-if="slotProps.data.required" class="text-red-500 ml-1" v-tooltip.top="'Required by the backend — every row needs a value.'">*</span>
              <span v-else-if="slotProps.data.recommended" class="text-primary ml-1"
                v-tooltip.top="'Required custom field — rows without it still import, but the detail stays blank.'">*</span>
            </template>
          </Column>
          <Column header="File Header">
            <template #body="slotProps">
              <Select v-model="headerMapping[slotProps.data.key]" :options="csvColumns"
                placeholder="Select Header" class="w-full" :showClear="true"
                :class="{ 'p-invalid': slotProps.data.required && !headerMapping[slotProps.data.key] }" />
            </template>
          </Column>
          <Column header="Preview (Row 1)">
            <template #body="slotProps">
              <span class="text-gray-600 text-sm">{{ getPreviewValue(slotProps.data.key) }}</span>
            </template>
          </Column>
        </DataTable>

        <div class="flex justify-end gap-2 mt-4">
          <span v-tooltip.top="!canSubmit ? submitDisabledReason : ''">
            <Button size="small" label="Preview Import" icon="pi pi-eye" @click="runPreview"
              :loading="previewingImport" :disabled="!canSubmit" data-testid="btn-student-import-preview" />
          </span>
        </div>
      </div>

      <!-- Preview Result Summary (hidden while review dialog is open) -->
      <div v-if="previewResult && !showReviewDialog" class="flex flex-col gap-4">
        <div class="flex flex-col sm:flex-row sm:items-center gap-4 p-4 rounded-lg border overflow-x-auto"
          :class="previewResult.failed ? 'bg-orange-50 border-orange-200' : 'bg-primary/10 border-primary/20'">
          <div class="flex items-center gap-6 flex-shrink-0">
            <div class="text-center">
              <p class="text-2xl font-bold text-gray-800">{{ previewResult.total_rows ?? 0 }}</p>
              <p class="text-xs text-gray-500">Total Rows</p>
            </div>
            <div class="text-center">
              <p class="text-2xl font-bold text-primary">{{ previewResult.successful ?? 0 }}</p>
              <p class="text-xs text-gray-500">Valid</p>
            </div>
            <div class="text-center" v-if="previewResult.failed">
              <p class="text-2xl font-bold text-red-600">{{ previewResult.failed }}</p>
              <p class="text-xs text-gray-500">Errors</p>
            </div>
          </div>
          <div class="flex gap-2 sm:ml-auto flex-shrink-0">
            <Button size="small" v-if="previewResult.failed"
              label="Review & Fix"
              icon="pi pi-pencil"
              class="p-button-outlined p-button-warning"
              @click="showReviewDialog = true" />
            <Button size="small" v-if="!previewResult.failed"
              label="Import Students"
              icon="pi pi-check"
              :loading="importingStudents"
              @click="confirmAndImport"
              data-testid="btn-student-import-submit" />
          </div>
        </div>
      </div>

      <!-- Queued (background) import progress -->
      <div
        v-if="queuedImport"
        class="flex flex-col gap-3 p-4 rounded-lg border"
        :class="{
          'bg-primary/10 border-primary/20': queuedProgressSeverity === 'info',
          'bg-green-50 border-green-200': queuedProgressSeverity === 'success',
          'bg-orange-50 border-orange-200': queuedProgressSeverity === 'warn',
          'bg-red-50 border-red-200': queuedProgressSeverity === 'danger',
        }"
        data-testid="student-import-queued-progress">
        <div class="flex items-center gap-3">
          <i class="pi text-2xl"
            :class="{
              'pi-spin pi-spinner text-primary': !queuedImportDone,
              'pi-check-circle text-green-600': queuedProgressSeverity === 'success',
              'pi-exclamation-triangle text-orange-600': queuedProgressSeverity === 'warn',
              'pi-times-circle text-red-600': queuedProgressSeverity === 'danger',
            }"></i>
          <div class="flex-1">
            <p class="font-semibold text-gray-800">Background Import</p>
            <p class="text-sm text-gray-600">{{ queuedProgressLabel }}</p>
          </div>
          <Button
            v-if="queuedImportDone"
            label="Dismiss"
            icon="pi pi-times"
            class="p-button-text p-button-sm"
            @click="dismissQueuedProgress"
          />
        </div>
        <ProgressBar
          v-if="!queuedImportDone || queuedImport.status === 'completed'"
          :value="queuedImport.progress"
          :showValue="true"
          class="h-3"
        />
        <div v-if="queuedImportDone && queuedImport.failed_rows > 0" class="flex justify-end">
          <Button
            label="Review Errors"
            icon="pi pi-pencil"
            class="p-button-outlined p-button-warning p-button-sm"
            @click="showReviewDialog = true"
          />
        </div>
      </div>

      <!-- Recent imports widget -->
      <StudentImportsList />

      <!-- Review & Edit Dialog -->
      <ImportReviewDialog
        v-model:visible="showReviewDialog"
        :studentRecords="studentRecords"
        :mappedFields="mappedFields"
        :rowErrorMap="rowErrorMap"
        :errorRowCount="errorRowCount"
        :validRowCount="validRowCount"
        :previewingImport="previewingImport"
        :importingStudents="importingStudents"
        :subjectsPerRow="subjectsPerRow"
        @revalidate="runPreview"
        @import="confirmAndImport"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import { useQuery } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import { useProgrammes } from '@/composables/queries/useProgrammes';
import { useCampuses } from '@/composables/queries/useCampuses';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { getClassroomSectionLabel } from '@/utils/classroomLabel';
import { useStudentMutations } from '@/composables/mutations/students/useStudentMutations';
import { useFileParser } from '@/composables/useFileParser';
import { useStudentImportPolling } from '@/composables/queries/useStudentImport';
import AcademicCalendarSelect from '@/components/common/AcademicCalendarSelect.vue';
import ImportReviewDialog from './ImportReviewDialog.vue';
import StudentImportsList from './StudentImportsList.vue';
import type { ImportError, ImportResponse, ProgrammeType, StudentImportRecordInput } from '@/types/composition-functions';
import { useCustomFields } from '@/composables/queries/useCustomFields';
import {
  IMPORT_PROFILE_FIELDS, autoMapImportHeaders, buildImportRecord, customImportFields, importErrorFieldKey,
  unmappedRecommendedFields, type ImportField,
} from '@/utils/studentDataFields';

const toast = useToast();
const confirm = useConfirm();

// Server caps the payload at 10,000 rows. The 500 split is just where the
// backend switches from synchronous to a queued background job.
const MAX_IMPORT_ROWS = 10_000;
const QUEUED_THRESHOLD = 500;

const {
  previewStudentImport, previewingImport,
  importStudents, importingStudents,
} = useStudentMutations();

const { record: queuedImport, isDone: queuedImportDone, start: startPolling, stop: stopPolling } = useStudentImportPolling();
const { parseFile, parsedRows, csvColumns, fileName, isParsing, resetParser } = useFileParser();

type Field = ImportField;

// ---- Config ----
const classroomSectionId = ref<number | null>(null);
const programmeId = ref<number | null>(null);
const campusId = ref<number | null>(null);
const academicCalendarId = ref<number | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
const previewResult = ref<ImportResponse | null>(null);
const showReviewDialog = ref(false);
const isDragging = ref(false);

const { classrooms } = useSchoolQuery();
const { isTertiary, schoolType } = useSchoolFeatures();
const { programmes, loadSchoolProgramme } = useProgrammes({ autoFetch: false, first: 200 });
const { campuses, loadCampuses } = useCampuses({ autoFetch: false });

watch(isTertiary, (val) => {
  if (val) { loadSchoolProgramme(); loadCampuses(); }
}, { immediate: true });

const levelOptions = computed(() =>
  (classrooms.value || []).map((c) => ({
    id: c?.classroomSections?.[0]?.id || '',
    name: c?.name || '',
  })),
);

const programmeOptions = computed(() =>
  programmes.value?.map((p: ProgrammeType) => ({
    label: `${p.program_name}${p.degree ? ` — ${p.degree}` : ''}${p.type ? ` — ${p.type}` : ''}`,
    id: p.id,
  })) || [],
);

const classroomSections = computed(() => {
  const sections: { id: string; name: string }[] = [];
  if (classrooms.value) {
    classrooms.value.forEach((classroom) => {
      if (classroom?.classroomSections?.length) {
        classroom.classroomSections.forEach((cs) => {
          sections.push({
            id: cs?.id || '',
            name: getClassroomSectionLabel(cs, classroom, schoolType.value),
          });
        });
      }
    });
  }
  const unique = new Map();
  sections.forEach((s) => unique.set(s.id, s));
  return Array.from(unique.values());
});

// ---- Mapping Logic ----
// `required` mirrors the backend rules in ProcessStudentImportJob.
// matric_no is only required when the school does NOT auto-generate matric numbers
// (see useMatricNoConfig below).
const { fields: studentCustomFields } = useCustomFields();
const modelFields = computed<Field[]>(() => [
  { key: 'first_name', label: 'First Name', required: true },
  { key: 'surname', label: 'Surname', required: true },
  { key: 'other_name', label: 'Other Name' },
  { key: 'matric_no', label: matricLabel.value, required: !autoMatricEnabled.value },
  { key: 'email', label: 'Email' },
  { key: 'dob', label: 'Date of Birth' },
  { key: 'gender', label: 'Gender', required: true },
  // Origin, address and the school's own student custom fields (#574).
  ...IMPORT_PROFILE_FIELDS,
  ...customImportFields(studentCustomFields.value),
]);
const headerMapping = ref<Record<string, string>>({});

// ---- School matric-no setting (controls whether matric_no is required) ----
const MATRIC_NO_CONFIG_QUERY = gql`
  query ImportMatricNoConfig {
    matricNoConfig { enabled }
  }
`;
const { result: matricConfigResult } = useQuery<{ matricNoConfig: { enabled: boolean } }>(MATRIC_NO_CONFIG_QUERY);
const autoMatricEnabled = computed(() => !!matricConfigResult.value?.matricNoConfig?.enabled);
const matricLabel = computed(() =>
  autoMatricEnabled.value ? 'Matric No / Admission No (auto-generated)' : 'Matric No / Admission No',
);

// ---- Warn before losing progress (refresh / close / navigate away) ----
const hasProgress = computed(() => parsedRows.value.length > 0);

function onBeforeUnload(e: BeforeUnloadEvent) {
  if (hasProgress.value) {
    e.preventDefault();
    e.returnValue = '';
  }
}

onMounted(() => window.addEventListener('beforeunload', onBeforeUnload));
onBeforeUnmount(() => window.removeEventListener('beforeunload', onBeforeUnload));

const mappedFields = computed(() =>
  modelFields.value.filter((f) => headerMapping.value[f.key]),
);

const requiredFields = computed(() => modelFields.value.filter((f) => f.required));

// Required custom fields are flagged but never block the import (#574).
const unmappedRecommended = computed(() => unmappedRecommendedFields(modelFields.value, headerMapping.value));
const unmappedRecommendedNames = computed(() =>
  unmappedRecommended.value.map((f) => f.label.replace(/^Custom: /, '')).join(', '),
);

const missingRequiredFields = computed(() =>
  requiredFields.value.filter((f) => !headerMapping.value[f.key]),
);

const canSubmit = computed(() => {
  if (!parsedRows.value.length) return false;
  if (missingRequiredFields.value.length > 0) return false;
  if (isTertiary.value) return !!programmeId.value && !!classroomSectionId.value;
  return !!classroomSectionId.value;
});

const submitDisabledReason = computed(() => {
  if (!parsedRows.value.length) return 'Upload a file first.';
  if (missingRequiredFields.value.length > 0) {
    const names = missingRequiredFields.value.map((f) => f.label).join(', ');
    return `Map these required field(s) first: ${names}.`;
  }
  if (isTertiary.value && !programmeId.value) return 'Select a programme.';
  if (!classroomSectionId.value) return isTertiary.value ? 'Select a level.' : 'Select a classroom section.';
  return '';
});

const studentRecords = ref<Record<string, string>[]>([]);

function rebuildStudentRecords() {
  studentRecords.value = parsedRows.value.map((row) => {
    const record: Record<string, string> = {};
    modelFields.value.forEach((field) => {
      const csvHeader = headerMapping.value[field.key];
      record[field.key] = csvHeader ? (row[csvHeader] || '').trim() : '';
    });
    return record;
  });
}

watch(headerMapping, () => {
  if (!showReviewDialog.value) {
    rebuildStudentRecords();
  }
}, { deep: true });

const triggerFileInput = () => fileInput.value?.click();

const getPreviewValue = (systemKey: string) => {
  const csvHeader = headerMapping.value[systemKey];
  if (csvHeader && parsedRows.value.length > 0) {
    return parsedRows.value[0]?.[csvHeader] || '-';
  }
  return '-';
};

// ---- File Upload Logic ----
function handleFile(file: File) {
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (!ext || !['csv', 'xls', 'xlsx'].includes(ext)) {
    toast.add({ severity: 'error', summary: 'Invalid File', detail: 'Please upload a CSV, XLS, or XLSX file.', life: 3000 });
    return;
  }

  parseFile(file, (columns) => {
    headerMapping.value = autoMapImportHeaders(columns, modelFields.value);
    previewResult.value = null;

    if (parsedRows.value.length > MAX_IMPORT_ROWS) {
      toast.add({ severity: 'warn', summary: 'Row Limit', detail: `Only the first ${MAX_IMPORT_ROWS.toLocaleString()} rows will be processed. Your file has ${parsedRows.value.length.toLocaleString()} rows.`, life: 5000 });
      parsedRows.value = parsedRows.value.slice(0, MAX_IMPORT_ROWS);
    }
  });
}

function onFileChange(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (file) {
    handleFile(file);
  }
  input.value = '';
}

function onDrop(event: DragEvent) {
  isDragging.value = false;
  const file = event.dataTransfer?.files?.[0];
  if (file) {
    handleFile(file);
  }
}

function downloadSampleCSV() {
  const headers = [
    ...modelFields.value.map((f) => f.label),
    'Subject 1',
    'Subject 2',
    'Subject 3',
  ];
  const csvContent = [headers, headers.map(() => '')].map((r) => r.join(',')).join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'sample_student_import.csv');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// ---- Error Tracking ----
// Map of row index → { field: errorMessage }
const rowErrorMap = ref<Record<number, Record<string, string>>>({});

function buildErrorMap(errors: ImportError[]) {
  const map: Record<number, Record<string, string>> = {};
  errors.forEach((e) => {
    if (e.row == null || !e.errors) return;
    const rowIndex = e.row - 1; // Backend rows are 1-indexed
    const fieldMap: Record<string, string> = {};
    if (typeof e.errors === 'object' && !Array.isArray(e.errors)) {
      for (const [field, msgs] of Object.entries(e.errors)) {
        fieldMap[importErrorFieldKey(field)] = Array.isArray(msgs) ? msgs.join(', ') : String(msgs);
      }
    }
    if (Object.keys(fieldMap).length) map[rowIndex] = fieldMap;
  });
  rowErrorMap.value = map;
}

const errorRowCount = computed(() => Object.keys(rowErrorMap.value).length);
const validRowCount = computed(() => studentRecords.value.length - errorRowCount.value);

// Any CSV column starting with "subject" (case-insensitive) becomes a subject value.
// e.g. "Subject 1", "subject_2", "SUBJECT", "Subject Name" all qualify.
const subjectColumns = computed(() =>
  csvColumns.value.filter((c) => c.trim().toLowerCase().startsWith('subject')),
);

function subjectsForRow(rowIndex: number): string[] {
  const original = parsedRows.value[rowIndex];
  if (!original) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const col of subjectColumns.value) {
    const val = (original[col] || '').trim();
    if (!val || seen.has(val.toLowerCase())) continue;
    seen.add(val.toLowerCase());
    out.push(val);
  }
  return out;
}

const subjectsPerRow = computed(() =>
  parsedRows.value.map((_, i) => subjectsForRow(i)),
);

// ---- Build import variables ----
function buildImportVars() {
  const students: StudentImportRecordInput[] = studentRecords.value.map((row, i) => buildImportRecord(row, subjectsForRow(i)));

  return {
    students,
    classroom_section_id: Number(classroomSectionId.value),
    programme_id: isTertiary.value && programmeId.value ? Number(programmeId.value) : undefined,
    academic_calendar_id: academicCalendarId.value ? Number(academicCalendarId.value) : undefined,
    campus_id: isTertiary.value && campusId.value ? Number(campusId.value) : undefined,
  };
}

// ---- Preview ----
async function runPreview() {
  if (missingRequiredFields.value.length > 0) {
    const names = missingRequiredFields.value.map((f) => f.label).join(', ');
    toast.add({
      severity: 'warn',
      summary: 'Required field mapping missing',
      detail: `Map a column for ${names} before previewing — every row would otherwise fail.`,
      life: 6000,
    });
    return;
  }

  if (!canSubmit.value) {
    toast.add({ severity: 'warn', summary: 'Validation', detail: submitDisabledReason.value || 'Please fill all required fields.', life: 4000 });
    return;
  }

  if (!showReviewDialog.value) {
    rebuildStudentRecords();
  }

  try {
    const result = await previewStudentImport(buildImportVars());
    if (result) {
      previewResult.value = result;
      if (result.errors?.length) {
        buildErrorMap(result.errors.filter(Boolean) as ImportError[]);
      } else {
        rowErrorMap.value = {};
      }

      showReviewDialog.value = true;
    }
  } catch {
    // Toast already handled in composable
  }
}

// ---- Final Import ----
function confirmAndImport() {
  const total = studentRecords.value.length;
  const willQueue = total > QUEUED_THRESHOLD;
  confirm.require({
    message: willQueue
      ? `You are about to import ${total.toLocaleString()} students. This is a large batch and will run in the background — you'll see live progress on this page. You can close the dialog but please keep the tab open until it finishes.`
      : `You are about to import ${total.toLocaleString()} students. This action cannot be undone.`,
    header: 'Confirm Import',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-success',
    accept: runFinalImport,
  });
}

function resetImportForm() {
  showReviewDialog.value = false;
  previewResult.value = null;
  rowErrorMap.value = {};
  studentRecords.value = [];
  resetParser();
  headerMapping.value = {};
  classroomSectionId.value = null;
  programmeId.value = null;
  campusId.value = null;
  academicCalendarId.value = null;
}

async function runFinalImport() {
  try {
    const result = await importStudents(buildImportVars());
    if (!result) return;

    // Queued: hand the import id to the poller and show the live progress block.
    if (result.queued && result.student_import_id) {
      showReviewDialog.value = false;
      previewResult.value = null;
      rowErrorMap.value = {};
      startPolling(result.student_import_id);
      return;
    }

    // Synchronous: original behavior — full rollback if any row fails.
    if (result.errors?.length) {
      previewResult.value = result;
      buildErrorMap(result.errors.filter(Boolean) as ImportError[]);
      showReviewDialog.value = true;
      toast.add({ severity: 'error', summary: 'Import Rolled Back', detail: `${result.failed} row(s) had errors — all ${result.total_rows} rows were rolled back. Fix the errors and try again.`, life: 8000 });
    } else {
      resetImportForm();
    }
  } catch {
    // Toast already handled in composable
  }
}

// ---- Queued progress display ----
const queuedProgressLabel = computed(() => {
  const r = queuedImport.value;
  if (!r) return '';
  switch (r.status) {
  case 'pending': return 'Queued — waiting for a worker…';
  case 'processing': return `Importing… ${r.successful_rows + r.failed_rows} of ${r.total_rows} processed`;
  case 'completed': return `Done — ${r.successful_rows.toLocaleString()} of ${r.total_rows.toLocaleString()} imported.`;
  case 'completed_with_errors': return `Finished with ${r.failed_rows.toLocaleString()} error${r.failed_rows === 1 ? '' : 's'}. ${r.successful_rows.toLocaleString()} of ${r.total_rows.toLocaleString()} imported.`;
  case 'failed': return r.error_message || 'Import failed.';
  default: return r.status;
  }
});

const queuedProgressSeverity = computed(() => {
  switch (queuedImport.value?.status) {
  case 'completed': return 'success';
  case 'completed_with_errors': return 'warn';
  case 'failed': return 'danger';
  default: return 'info';
  }
});

// When the queued import finishes, expose its errors via the existing review dialog.
watch(queuedImportDone, (done) => {
  if (!done || !queuedImport.value) return;
  const r = queuedImport.value;
  if (r.status === 'completed') {
    toast.add({ severity: 'success', summary: 'Import Complete', detail: `${r.successful_rows.toLocaleString()} students imported.`, life: 5000 });
    return;
  }
  if (r.status === 'completed_with_errors' && Array.isArray(r.errors)) {
    previewResult.value = {
      total_rows: r.total_rows,
      successful: r.successful_rows,
      failed: r.failed_rows,
      errors: r.errors as ImportError[],
    } as ImportResponse;
    buildErrorMap((r.errors as ImportError[]).filter(Boolean));
    toast.add({ severity: 'warn', summary: 'Import finished with errors', detail: `${r.successful_rows.toLocaleString()} succeeded, ${r.failed_rows.toLocaleString()} failed. Click "Review & Fix" to see details.`, life: 8000 });
  }
  if (r.status === 'failed') {
    toast.add({ severity: 'error', summary: 'Import Failed', detail: r.error_message || 'Background import failed.', life: 8000 });
  }
});

function dismissQueuedProgress() {
  if (queuedImport.value && queuedImportDone.value && queuedImport.value.status === 'completed') {
    resetImportForm();
  }
  stopPolling();
}

onBeforeUnmount(() => stopPolling());
</script>
